import React from 'react';
import type { FormPackage, FormPackageDraft, FormPackageFormOption } from '../../forms/types';
import { cn } from '../../lib/cn';
import { Button } from '../Button/Button';
import { Checkbox } from '../Checkbox/Checkbox';
import { Icon } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';
import { Input } from '../Input/Input';
import { ViewportDrawer } from '../Overlay/ViewportDrawer';
import { Textarea } from '../Textarea/Textarea';

export interface FormPackagesDrawerProps {
  open: boolean;
  onClose: () => void;
  packages: FormPackage[];
  formOptions: FormPackageFormOption[];
  view: 'list' | 'edit';
  editingId: string | null;
  draft: FormPackageDraft;
  onDraftChange: (draft: FormPackageDraft) => void;
  onSelectPackage: (id: string) => void;
  onCreateNew: () => void;
  onBackToList: () => void;
  onSave: () => void;
  onDelete?: () => void;
  isLoading?: boolean;
  isSaving?: boolean;
}

function formCountLabel(count: number): string {
  if (count === 1) return '1 form';
  return `${count} forms`;
}

export function FormPackagesDrawer({
  open,
  onClose,
  packages,
  formOptions,
  view,
  editingId,
  draft,
  onDraftChange,
  onSelectPackage,
  onCreateNew,
  onBackToList,
  onSave,
  onDelete,
  isLoading = false,
  isSaving = false,
}: FormPackagesDrawerProps) {
  const isCreate = view === 'edit' && editingId == null;
  const selectedCount = draft.formIds.length;

  const toggleForm = (formId: string) => {
    const next = draft.formIds.includes(formId)
      ? draft.formIds.filter((id) => id !== formId)
      : [...draft.formIds, formId];
    onDraftChange({ ...draft, formIds: next });
  };

  return (
    <ViewportDrawer open={open} onClose={onClose} width="min(460px, 92vw)">
      <div className="flex h-full flex-col">
        <div className="flex items-center gap-2.5 border-b border-[var(--cream-2)] px-5 py-4">
          {view === 'edit' ? (
            <IconButton icon="left" size={30} onClick={onBackToList} label="Back to packages" />
          ) : (
            <Icon kind="folder" size={14} color="var(--ink-3)" />
          )}
          <span className="font-[var(--font-display)] text-[15px] font-semibold tracking-[-0.015em] text-[var(--ink-0)]">
            {view === 'list' ? 'Packages' : isCreate ? 'New package' : 'Edit package'}
          </span>
          <span className="ml-auto">
            <IconButton icon="x" size={30} onClick={onClose} label="Close" />
          </span>
        </div>

        {view === 'list' ? (
          <>
            <div className="cal-scroll flex-1 overflow-y-auto px-5 py-4">
              {isLoading ? (
                <p className="py-8 text-center text-sm text-[var(--ink-4)]">Loading packages…</p>
              ) : packages.length === 0 ? (
                <div className="rounded-[var(--r-lg)] border border-dashed border-[var(--cream-2)] bg-[var(--cream-0)] px-4 py-10 text-center">
                  <Icon kind="folder" size={24} className="mx-auto mb-3 text-[var(--ink-4)]" />
                  <p className="text-sm font-medium text-[var(--ink-1)]">No packages yet</p>
                  <p className="mt-1 text-xs text-[var(--ink-4)]">
                    Group forms into packages and set when they should send together.
                  </p>
                </div>
              ) : (
                <ul className="flex flex-col gap-2">
                  {packages.map((pkg) => (
                    <li key={pkg.id}>
                      <button
                        type="button"
                        onClick={() => {
                          onSelectPackage(pkg.id);
                        }}
                        className="flex w-full items-start gap-3 rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3.5 py-3 text-left transition hover:border-[var(--cream-shadow)] hover:bg-[var(--cream-0)]"
                      >
                        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--r-sm)] bg-[var(--ai-tint)]">
                          <Icon kind="folder" size={15} className="text-[var(--ai-3)]" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-[var(--ink-0)]">
                            {pkg.name}
                          </span>
                          {pkg.description ? (
                            <span className="mt-0.5 block line-clamp-2 text-xs text-[var(--ink-3)]">
                              {pkg.description}
                            </span>
                          ) : null}
                          <span className="mt-1.5 flex flex-wrap items-center gap-2">
                            <span className="font-[var(--font-mono)] text-[10.5px] text-[var(--ink-4)]">
                              {formCountLabel(pkg.forms.length)}
                            </span>
                            {pkg.trigger ? (
                              <span className="truncate text-[10.5px] text-[var(--ink-3)]">
                                · {pkg.trigger}
                              </span>
                            ) : null}
                          </span>
                        </span>
                        <Icon kind="right" size={14} className="mt-2 shrink-0 text-[var(--ink-4)]" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="border-t border-[var(--cream-2)] px-5 py-4">
              <Button variant="primary" size="sm" iconLeft="plus" className="w-full" onClick={onCreateNew}>
                New package
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="cal-scroll flex-1 overflow-y-auto px-5 py-4">
              <div className="flex flex-col gap-4">
                <Input
                  label="Name"
                  value={draft.name}
                  onChange={(e) => {
                    onDraftChange({ ...draft, name: e.target.value });
                  }}
                  placeholder="Package name"
                  autoFocus
                />
                <Textarea
                  label="Description"
                  value={draft.description}
                  onChange={(e) => {
                    onDraftChange({ ...draft, description: e.target.value });
                  }}
                  placeholder="What is this package for?"
                  rows={3}
                />
                <Textarea
                  label="Trigger rules"
                  value={draft.trigger}
                  onChange={(e) => {
                    onDraftChange({ ...draft, trigger: e.target.value });
                  }}
                  placeholder="e.g. Send to all new patients before their first appointment"
                  hint="Describe when ELVA should send this package automatically."
                  rows={4}
                />

                <div>
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <span className="text-[12.5px] font-semibold text-[var(--ink-1)]">Forms</span>
                    <span className="font-[var(--font-mono)] text-[10.5px] text-[var(--ink-4)]">
                      {selectedCount} selected
                    </span>
                  </div>
                  {formOptions.length === 0 ? (
                    <p className="rounded-[var(--r-md)] border border-[var(--cream-2)] px-3 py-4 text-xs text-[var(--ink-4)]">
                      No forms in your library yet. Create a form first, then add it here.
                    </p>
                  ) : (
                    <ul className="max-h-[240px] overflow-y-auto rounded-[var(--r-md)] border border-[var(--cream-2)]">
                      {formOptions.map((form) => {
                        const checked = draft.formIds.includes(form.id);
                        return (
                          <li
                            key={form.id}
                            className={cn(
                              'border-b border-[var(--cream-2)] last:border-b-0',
                              checked && 'bg-[color-mix(in_srgb,var(--ai-tint)_35%,var(--cream-1))]',
                            )}
                          >
                            <button
                              type="button"
                              onClick={() => {
                                toggleForm(form.id);
                              }}
                              className="flex w-full cursor-pointer items-start gap-3 px-3 py-2.5 text-left transition hover:bg-[color-mix(in_srgb,var(--ai-tint)_20%,var(--cream-1))]"
                            >
                              <span className="pointer-events-none pt-0.5">
                                <Checkbox checked={checked} />
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="block text-sm text-[var(--ink-0)]">{form.name}</span>
                                {form.desc ? (
                                  <span className="mt-0.5 block text-xs text-[var(--ink-4)]">{form.desc}</span>
                                ) : null}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 border-t border-[var(--cream-2)] px-5 py-4">
              {!isCreate && onDelete ? (
                <Button variant="ghost" size="sm" disabled={isSaving} onClick={onDelete}>
                  Delete
                </Button>
              ) : (
                <span />
              )}
              <span className="ml-auto flex gap-2">
                <Button variant="secondary" size="sm" disabled={isSaving} onClick={onBackToList}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  iconLeft="check"
                  disabled={isSaving || !draft.name.trim()}
                  onClick={onSave}
                >
                  {isSaving ? 'Saving…' : 'Save package'}
                </Button>
              </span>
            </div>
          </>
        )}
      </div>
    </ViewportDrawer>
  );
}
